import React, { useState, useEffect } from "react";
import { FaIdCard } from "react-icons/fa";
import { MdHourglassEmpty, MdFormatListBulleted } from "react-icons/md";
import {
  HiOutlineDownload,
  HiOutlineLogin,
  HiOutlineShieldCheck,
  HiOutlineShoppingBag,
} from "react-icons/hi";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import Swal from "sweetalert2";

import UtiPanCardService from "../../../services/UtiPanCardService";
import storage from "../../../utils/storage";

const UtiPanCard = () => {
  const [loading, setLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState("");

  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);
      await fetchServiceStatus();
      await checkSubscriptionStatus();
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceStatus = async () => {
    try {
      const result = await UtiPanCardService.serviceStatus();

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res?.status === "SUCCESS") {
        const status = (res?.data?.service_status || "")
          .toString()
          .trim()
          .toUpperCase();

        console.log("UTI SERVICE STATUS:", status);
        setServiceStatus(status);
      }

      if (res.message == "Unauthorized Access") {
        storage.clearStorage();
        window.location.href = "/";
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    }
  };

  // 🔐 SAME LOGIC AS NSDL
  const canAccessService = () => {
    if (serviceStatus === "ENABLED") return true;
    return premiumStatus === "true";
  };

  const services = [
    {
      name: "Agent Registration",
      icon: <FaIdCard size={40} color="#0d6efd" />,
      href: "/uti-pancard/agent-registration",
      newTab: false,
    },
    {
      name: "View Credentials",
      icon: <HiOutlineShieldCheck size={40} color="#0d6efd" />,
      href: "/uti-pancard/view-credentials",
      newTab: false,
    },
    {
      name: "PSA Login",
      icon: <HiOutlineLogin size={40} color="#0d6efd" />,
      href: "https://www.psaonline.utiitsl.com/psapanservices/forms/login.html/loginHome",
      newTab: true,
    },
    {
      name: "Buy PAN Card Coupon",
      icon: <HiOutlineShoppingBag size={40} color="#0d6efd" />,
      href: "/uti-pancard/buy-coupon",
      newTab: false,
    },
    {
      name: "PAN Coupon History",
      icon: <MdFormatListBulleted size={40} color="#0d6efd" />,
      href: "/uti-pancard/pan-coupon-history",
    },
    //  🆕 Aadhaar to PAN
    {
      name: "Aadhaar Number to PAN Number",
      icon: <FaIdCard size={40} color="#198754" />,
      href: "/aadhaar-to-pan",
      newTab: false,
      isNew: true,
    },

    {
      name: "PAN Status",
      icon: <MdHourglassEmpty size={40} color="#0d6efd" />,
      href: "https://www.trackpan.utiitsl.com/PANONLINE/forms/TrackPan/trackApp",
      newTab: true,
    },
    {
      name: "e-PAN Download",
      icon: <HiOutlineDownload size={40} color="#0d6efd" />,
      href: "https://www.pan.utiitsl.com/PAN_ONLINE/ePANCardHome",
      newTab: true,
    },
  ];

  return (
    <div>
      {loading && <LoadingScreen />}

      <div className="container-fluid mt-3">
        <div className="row">
          <SideBar />

          <div className="col-md-9 mb-3">
            <div className="card mb-4 shadow-sm">
              <div className="card-body">
                <h5 className="card-title mb-4">UTIITSL PAN Card Services</h5>

                <div className="d-flex gap-4 flex-wrap justify-content-center">
                  {services.map((service, index) => (
                    <div
                      key={index}
                      className="border rounded p-4 text-center shadow-sm hover-effect"
                      style={{
                        width: "220px",
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.6 : 1,
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => {
                        if (loading) return;

                        if (canAccessService()) {
                          service.newTab
                            ? window.open(service.href, "_blank")
                            : (window.location.href = service.href);
                        } else {
                          setShowDialog(true);
                        }
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.transform = "scale(1.05)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.transform = "scale(1)")
                      }
                    >
                      <div className="mb-3">{service.icon}</div>
                      <div className="fw-medium" style={{ fontSize: "16px" }}>
                        {service.name}{" "}
                        {service.isNew && (
                          <span
                            className="badge bg-danger ms-2"
                            style={{ fontSize: "10px" }}
                          >
                            NEW
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="card shadow-sm mt-4">
              <div className="card-body">
                <h5 className="card-title mb-3">Video Tutorials</h5>

                {/* ===== E-KYC MODE ROW ===== */}
                <h6 className="mb-2 text-primary pt-3">
                  Agent Registration & PAN Coupon Purchase
                </h6>
                <div className="d-flex gap-4 flex-wrap justify-content-center mb-4">
                  {/* New PAN - E-KYC */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1NOmitfgkyfTyDMpimCgQ6iNQgequ7Rp5/view?usp=sharing",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">Login process</div>
                  </div>

                  {/* Correction PAN - E-KYC */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1dmURUuH155e_sRX8PQ3VHTFr_zuUUx9a/view?usp=sharing",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">Buy PAN Coupon</div>
                  </div>
                </div>

                {/* ===== E-SIGN MODE ROW ===== */}
                <h6 className="mb-2 text-success">PAN Application</h6>
                <div className="d-flex gap-4 flex-wrap justify-content-center">
                  {/* New PAN - E-Sign */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/16dk-cNC_FlEcb6W4mGiTwym4qBO0XQvq/view?usp=sharing",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">New PAN Apply</div>
                  </div>

                  {/* Correction PAN - E-Sign */}
                  <div
                    className="border rounded p-3 text-center shadow-sm hover-effect"
                    style={{ width: "260px", cursor: "pointer" }}
                    onClick={() =>
                      window.open(
                        "https://drive.google.com/file/d/1Av1x0qpcky307AsoyDwlW5fuaImSVUgU/view?usp=sharing",
                        "_blank",
                      )
                    }
                  >
                    <div className="fw-medium">PAN Correction</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SubscriptionDialog
        show={showDialog && serviceStatus !== "ENABLED"}
        onHide={() => setShowDialog(false)}
        serviceName="UTI PAN"
      />
    </div>
  );
};

export default UtiPanCard;
